import { formOptions } from '@tanstack/react-form';
import { z } from 'zod';

import { notificationChannelSchema } from '@/entities/notification';
import { SCHEDULE_PERIOD, WEEKDAY } from '@/types/commonConst';
import {
  WORKFLOW_CHANNEL_TYPE,
  WORKFLOW_COMPARE_DELTA,
  WORKFLOW_PERIODIC_BASIS,
  WORKFLOW_PERIODIC_TRIGGER_TYPE,
} from '@/types/workflowConst';

// 스케줄 스키마
const scheduleSchema = z.object({
  hour: z.number().min(0).max(23),
  period: z.nativeEnum(SCHEDULE_PERIOD),
  weekday: z.nativeEnum(WEEKDAY).optional(),
  date: z.number().min(1).max(28).optional(),
});

// Periodic 폼 스키마 (backend PeriodicWorkflowState + base fields 구조와 일치)
export const periodicFormSchema = z
  .object({
    title: z.string().min(1, { message: 'Please enter a title' }),
    channel: notificationChannelSchema,
    // PeriodicWorkflowState 필드들
    basis: z.nativeEnum(WORKFLOW_PERIODIC_BASIS).optional(),
    all: z.boolean(),
    basis_list: z.array(z.string()),
    timezone: z.string().min(1),
    schedule: scheduleSchema,
    // UI-only 필드: trigger 모드 선택 (API에는 percentage 유무로 구분)
    triggerType: z.nativeEnum(WORKFLOW_PERIODIC_TRIGGER_TYPE),
    percentage: z.number().min(1).optional(),
    numerical: z.number().min(1).optional(),
    delta: z.nativeEnum(WORKFLOW_COMPARE_DELTA),
  })
  .refine(
    // all이 false면 basis_list에 최소 1개 항목 필요
    (data) => data.all || data.basis_list.length > 0,
    {
      message: 'Please select at least one item',
      path: ['basis_list'],
    },
  );

export type PeriodicFormData = z.infer<typeof periodicFormSchema>;

// 기본값 (timezone은 사용자 시스템 기준)
export const periodicFormDefaultValues: PeriodicFormData = {
  title: '',
  channel: {
    type: WORKFLOW_CHANNEL_TYPE.SLACK,
    slack_webhook_id: '',
  },
  basis: undefined,
  all: true,
  basis_list: [],
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  schedule: {
    period: SCHEDULE_PERIOD.DAILY,
    hour: 9,
  },
  triggerType: WORKFLOW_PERIODIC_TRIGGER_TYPE.PERCENTAGE,
  percentage: 20,
  numerical: 100,
  delta: WORKFLOW_COMPARE_DELTA.INCREASE,
};

// 폼 검증 함수
const validatePeriodicForm = ({ value }: { value: PeriodicFormData }) => {
  const result = periodicFormSchema.safeParse(value);
  if (result.success) return undefined;

  return result.error.issues.map((issue) => ({
    path: issue.path,
    message: issue.message,
  }));
};

export const periodicFormOptions = formOptions({
  defaultValues: periodicFormDefaultValues,
  validators: {
    onSubmit: validatePeriodicForm,
  },
});
